import React, { useEffect, useState } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Button } from '@mui/material';
import axios from '../api/axios';

interface ConsolidatedItem {
  sku: string;
  name: string;
  quantity: number;
  price: number;
  weight: number;
}

const ConsolidatedOrder: React.FC = () => {
  // Breadcrumb menu for navigation
  const Breadcrumb = () => (
    <Box sx={{ mb: 2, mt: 2 }}>
      <Button
        variant="text"
        color="primary"
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16 }}
        component={require('react-router-dom').Link}
        to="/admin"
        startIcon={<span style={{ fontSize: 18 }}>🏠</span>}
      >
        Admin Menu
      </Button>
      <Typography component="span" sx={{ mx: 1, fontWeight: 600, color: 'text.secondary' }}>
        /
      </Typography>
      <Typography component="span" sx={{ fontWeight: 600, color: 'primary.main', fontSize: 16 }}>
        Consolidated Order
      </Typography>
    </Box>
  );
  const [items, setItems] = useState<ConsolidatedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    // Load latest order batch
    axios.get('/orderbatch/current')
      .then((response) => {
        const batchId = response.data?.batchId;
        if (!batchId) {
          setError('No active order batch.');
          setLoading(false);
          return;
        }
        // Call consolidated endpoint
        axios.get(`/order/consolidated?batchId=${batchId}`)
          .then((itemResponse) => {
            setItems(itemResponse.data);
            setLoading(false);
          })
          .catch(() => {
            setError('Failed to load consolidated order.');
            setLoading(false);
          });
      })
      .catch(() => {
        setError('Failed to load current order batch.');
        setLoading(false);
      });
  }, []);

  const grandTotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const totalWeight = items.reduce((sum, item) => sum + ((item.weight || 0) * item.quantity), 0);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Breadcrumb />
      <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
        Consolidated Order Form
      </Typography>
      {error && (
        <Typography color="error" align="center" sx={{ mb: 2 }}>{error}</Typography>
      )}
      {loading ? (
        <Typography align="center">Loading...</Typography>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={item.sku + idx}>
                    <TableCell>{item.sku}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{`$${(item.price * item.quantity).toFixed(2)}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Grand Total: ${grandTotal.toFixed(2)}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Total Weight: {totalWeight.toFixed(2)} lbs
            </Typography>
          </Box>
          {/* Removed Return to Admin Menu button as requested */}
        </>
      )}
    </Container>
  );
};

export default ConsolidatedOrder;
